<template>
  <ContentSection>
    <!-- TITLE & TEXT -->
    <HeadingAndText :heading="data.heading" :text="data.text" />

    <div class="video-player">
      <ClientOnly>
        <VuePlyr v-if="isVimeo">
          <div

            data-plyr-provider="vimeo"
            :data-plyr-embed-id="videoId"
          />
        </VuePlyr>
        <VuePlyr v-if="isYoutube" :options="{ youtube: { noCookie: true } }">
          <div
            :data-plyr-embed-id="videoId"
            data-plyr-provider="youtube"
          />
        </VuePlyr>
      </ClientOnly>
    </div>
  </ContentSection>
</template>

<script setup lang="ts">
import 'plyr/dist/plyr.css'
import type { MainComponentsVideoFragment } from '#gql'

const props = defineProps<{ data: Enforce<MainComponentsVideoFragment> }>()

// register the VuePlyr component
const VuePlyr = defineAsyncComponent(() => import('@skjnldsv/vue-plyr')) as any

const videoId = computed(() => {
  return useGetVideoEmbedUrl(props.data.video, true)
})

const isYoutube = computed(() => {
  return /(?:youtube\.com|youtu\.be)/.test(props.data.video || '')
})

const isVimeo = computed(() => {
  return /vimeo\.com/.test(props.data.video || '')
})
</script>

<style scoped>
.video-player {
  --video-color-light: theme('colors.gray.100');
  --video-color-dark: theme('colors.black');
  --video-color-accent: theme('colors.red.500');

  --plyr-color-main: var(--video-color-light);
  --plyr-video-controls-background: linear-gradient(
    transparent,
    color-mix(in srgb, var(--video-color-dark) 80%, transparent)
  );
  --plyr-video-control-background: var(--video-color-accent);
  --plyr-video-control-background-hover: var(--video-color-light);
  --plyr-video-control-color: var(--video-color-light);
  --plyr-video-control-color-hover: var(--video-color-accent);

  --plyr-range-thumb-background: var(--video-color-light);
  --plyr-progress-loading-background: color-mix(
    in srgb,
    var(--video-color-light) 50%,
    transparent
  );
  --plyr-video-progress-buffered-background: color-mix(
    in srgb,
    var(--video-color-light) 50%,
    transparent
  );

  --plyr-menu-background: var(--video-color-accent);
  --plyr-menu-color: var(--video-color-light);
  --plyr-menu-item-arrow-color: var(--video-color-light);
  --plyr-menu-border-color: var(--video-color-light);

  --plyr-tooltip-background: var(--video-color-light);
  --plyr-tooltip-color: var(--video-color-accent);
  --plyr-font-family: theme('fontFamily.sans');

  /* Bug fix in plyr control colors */
  & :deep(.plyr__control--overlaid) {
    background: var(--plyr-video-control-background);
  }
  & :deep(.plyr__control--overlaid):hover {
    background: var(--plyr-video-control-background-hover);
  }

  & :deep(.plyr__video-embed__iframe) {
    display: none !important;
  }
  & :deep(.plyr iframe[id^='youtube']) {
    top: -50%;
    height: 200%;
  }

  & :deep(iframe) {
    pointer-events: none;
  }

}

@media screen and (max-width: 479px) {
  .video-player {
    & :deep(.plyr__volume) {
      display: none;
    }
  }
}

@media screen and (min-width: 768px) {
  .video-player {
    --plyr-control-icon-size: 36px;

  }
};
</style>
